import type { APIClient } from "@heidelbergmaterials/fcl-apiclient"
import { spawnErrorToast } from "@hm/fundamental"
import { createDeclaration, getMaterialById } from "@shared/api"
import i18next from "i18next"
import { create } from "zustand"
import { immer } from "zustand/middleware/immer"

import {
	DeclarationDetailsFormInputs,
	DeclarationDetailsFormState,
	DeclarationType,
	SelectionOfDeclarationFormInputs,
	SelectionOfDeclarationTypeFormState,
	SelectionOfMaterialFormInputs,
	SelectionOfMaterialFormState,
} from "./types"

type State = {
	materialDescription?: string
	materialNumber?: string
	contractNumber?: string
	selectionOfDeclarationTypeFormInputs?: SelectionOfDeclarationFormInputs
	selectionOfMaterialFormInputs?: SelectionOfMaterialFormInputs
	declarationDetailsFormInputs?: DeclarationDetailsFormInputs
	selectionOfDeclarationTypeFormState?: SelectionOfDeclarationTypeFormState
	selectionOfMaterialFormState?: SelectionOfMaterialFormState
	declarationDetailsFormState?: DeclarationDetailsFormState
}

type Actions = {
	fetchDefaultMaterial: (
		apiClient: APIClient,
		contractNumber: string,
		materialNumber: string,
	) => Promise<void>
	setDefaultMaterial: (
		materialDescription: string,
		materialNumber: string,
		contractNumber: string,
	) => void
	setSelectionOfDeclarationTypeFormInputs: (
		formInputs: SelectionOfDeclarationFormInputs,
	) => void
	setSelectionOfMaterialFormInputs: (
		formInputs: SelectionOfMaterialFormInputs,
	) => void
	setDeclarationDetailsFormInputs: (
		formInputs: DeclarationDetailsFormInputs,
	) => void
	setSelectionOfDeclarationTypeFormState: (
		formInputs: SelectionOfDeclarationFormInputs,
	) => void
	setSelectionOfMaterialFormState: (
		formState: SelectionOfMaterialFormState,
	) => void
	setDeclarationDetailsFormState: (
		formInputs: DeclarationDetailsFormInputs,
	) => void
	submitDeclaration: (apiClient: APIClient) => Promise<string | undefined>
	resetDeclarationData: () => void
}

export const useDeclarationCreationFormStore = create<State & Actions>()(
	immer((set, get) => ({
		materialDescription: undefined,
		materialNumber: undefined,
		contractNumber: undefined,

		selectionOfDeclarationTypeFormInputs: undefined,
		selectionOfMaterialFormInputs: undefined,
		declarationDetailsFormInputs: undefined,

		selectionOfDeclarationTypeFormState: undefined,
		selectionOfMaterialFormState: undefined,
		declarationDetailsFormState: undefined,

		fetchDefaultMaterial: async (
			apiClient: APIClient,
			contractNumber: string,
			materialNumber: string,
		) => {
			try {
				const material = await getMaterialById(
					apiClient,
					contractNumber,
					materialNumber,
				)

				set(state => {
					state.materialDescription = material.description
					state.materialNumber = material.materialNumber
					state.contractNumber = material.contract.number
				})
			} catch {
				spawnErrorToast(
					i18next.t(
						"shared:useDeclarationCreationFormStore.errorMessages.fetchDefaultMaterial",
					),
				)
			}
		},
		setDefaultMaterial: (
			materialDescription: string,
			materialNumber: string,
			contractNumber: string,
		) =>
			set(state => {
				state.materialDescription = materialDescription
				state.materialNumber = materialNumber
				state.contractNumber = contractNumber
			}),

		setSelectionOfDeclarationTypeFormInputs: (
			formInputs: SelectionOfDeclarationFormInputs,
		) =>
			set(state => {
				state.selectionOfDeclarationTypeFormInputs = formInputs
			}),
		setSelectionOfMaterialFormInputs: (
			formInputs: SelectionOfMaterialFormInputs,
		) =>
			set(state => {
				state.selectionOfMaterialFormInputs = formInputs
			}),
		setDeclarationDetailsFormInputs: (
			formInputs: DeclarationDetailsFormInputs,
		) =>
			set(state => {
				state.declarationDetailsFormInputs = formInputs
			}),

		setSelectionOfDeclarationTypeFormState: (
			formInputs: SelectionOfDeclarationFormInputs,
		) => {
			const project = formInputs.project || formInputs.newProject

			if (!project) {
				return
			}

			set(state => {
				state.selectionOfDeclarationTypeFormState = {
					declarationType: formInputs.declarationType,
					projectId: formInputs.projectId,
					project: project,
				}
			})
		},
		setSelectionOfMaterialFormState: (
			formState: SelectionOfMaterialFormState,
		) =>
			set(state => {
				state.selectionOfMaterialFormState = formState
			}),
		setDeclarationDetailsFormState: (
			formInputs: DeclarationDetailsFormInputs,
		) => {
			const shouldNotSetState =
				!formInputs.country ||
				!formInputs.project ||
				!formInputs.companyName ||
				!formInputs.street ||
				!formInputs.zipCode ||
				!formInputs.city

			if (shouldNotSetState) {
				return
			}

			set(state => {
				state.declarationDetailsFormState = {
					project: formInputs.project!,
					companyName: formInputs.companyName!,
					street: formInputs.street!,
					zipCode: formInputs.zipCode!,
					city: formInputs.city!,
					country: formInputs.country!,
				}
			})
		},
		submitDeclaration: async (apiClient: APIClient) => {
			try {
				const declarationCreationState = get()

				const shouldNotSubmit =
					!declarationCreationState.declarationDetailsFormState ||
					!declarationCreationState.selectionOfMaterialFormState ||
					!declarationCreationState.selectionOfDeclarationTypeFormState ||
					!declarationCreationState.materialNumber ||
					!declarationCreationState.contractNumber

				if (shouldNotSubmit) {
					return
				}

				const response = await createDeclaration(apiClient, {
					projectReference: {
						projectReferenceId:
							declarationCreationState
								.selectionOfDeclarationTypeFormState
								?.projectId || undefined,
						customerId:
							declarationCreationState
								.selectionOfMaterialFormState!.customerId,

						reference:
							declarationCreationState
								.declarationDetailsFormState!.project,
						isInternal:
							declarationCreationState
								.selectionOfDeclarationTypeFormState!
								.declarationType ===
							DeclarationType.InternalProject,
					},
					declarationHolder: {
						companyName:
							declarationCreationState
								.declarationDetailsFormState!.companyName,
						street: declarationCreationState
							.declarationDetailsFormState!.street,
						postalCode:
							declarationCreationState
								.declarationDetailsFormState!.zipCode,
						city: declarationCreationState
							.declarationDetailsFormState!.city,
						country:
							declarationCreationState
								.declarationDetailsFormState!.country,
					},
					contractNumber:
						declarationCreationState.selectionOfMaterialFormState!
							.contractNumber,
					materialNumber:
						declarationCreationState.selectionOfMaterialFormState!
							.materialNumber,
					deliveredQuantity:
						declarationCreationState.selectionOfMaterialFormState!
							.quantity,
					deliveredUnit:
						declarationCreationState.selectionOfMaterialFormState!
							.unit,
					nonTransferableRaw:
						declarationCreationState.selectionOfMaterialFormState!
							.nonTransferableSavings,
				})

				return response.projectId
			} catch {
				spawnErrorToast(
					i18next.t(
						"shared:useDeclarationCreationFormStore.errorMessages.submitDeclaration",
					),
				)
			}
		},
		resetDeclarationData: () =>
			set(state => {
				state.materialDescription = undefined
				state.materialNumber = undefined
				state.selectionOfDeclarationTypeFormInputs = undefined
				state.selectionOfMaterialFormInputs = undefined
				state.declarationDetailsFormInputs = undefined
				state.selectionOfDeclarationTypeFormState = undefined
				state.selectionOfMaterialFormState = undefined
				state.declarationDetailsFormState = undefined
			}),
	})),
)
